import React, { useEffect } from 'react';

import Links from 'constants/links';
import { Typography, Anchor } from '@ampol/design-system';
import './rsa-terms-and-conditions-content.scss';

/**
 * Terms of Use Page
 * @returns Terms of Use
 */
const RsaTermsAndConditionsContent = ({ vehicleTypeSelected }) => {

  useEffect(() => {
    if (vehicleTypeSelected) {
      const sectionToScroll = vehicleTypeSelected === 'Passenger' ? 'section-2' : 'section-3';

      const targetElement = document.getElementById(sectionToScroll);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  }, []);

  return (
    <div className="terms-and-conditions-content">
      <div>
        <Typography component="h4" id="section-1">AmpolCard Facility and Bulk Standard Terms and Conditions</Typography>
        <Typography component="small">
          <strong>1. Acceptance of Application</strong>
        </Typography>
        <Typography component="small">
          1.1 Ampol may accept or decline an Application (at its sole discretion) by written notice to the Customer
          after the receipt of the correctly completed Application and all supporting information that may be
          requested.&nbsp;
        </Typography>
        <Typography component="small">
          1.2 If Ampol accepts an Application, Ampol agrees to provide the Customer with the AmpolCard Facility and/or
          Bulk Facility on these Standard Terms and Conditions.
        </Typography>
        <Typography component="small">
          1.3 In consideration of Ampol providing the Customer with the AmpolCard Facility and/or Bulk Facility, the
          Customer agrees to be bound by these Standard Terms and Conditions (except where Ampol and the Customer are
          parties to a separate agreement for the supply of:
          <br />
          (a) Bulk Facility;
          <br />
          (b) AmpolCard Facility; or
          <br />
          (c) Bulk Facility and AmpolCard Facility in addition to being a signatory to the Application, in which case,
          that separate agreement will constitute the agreement between Ampol and the Customer for:
          <br />
          (a) Bulk Facility
          <br />
          (b) AmpolCard Facility; or
          <br />
          (c) both the Bulk and AmpolCard Facility as the case may be, and these Standard Terms and Conditions (or the
          relevant section of these Standard Terms and Conditions) will not apply).
        </Typography>
        <Typography component="small">
          1.4 These Standard Terms and Conditions include the Application, the Direct Debit Service Agreement and the
          Guarantee Indemnity and Charge.
        </Typography>
        <Typography component="small">
          <strong>2. Term</strong>
        </Typography>
        <Typography component="small">
          2.1 These Standard Terms and Conditions commence when Ampol accepts the Customer&apos;s Application and continue
          until terminated in accordance with clause 9.
        </Typography>
        <Typography component="small">
          <strong>3. Credit Limit</strong>
        </Typography>
        <Typography component="small">3.1 The Customer must not exceed its Credit Limit.</Typography>
        <Typography component="small">
          3.2 Ampol may refuse to supply Products or Bulk Products or to process a Transaction for Services to the
          Customer /Cardholder in excess of the Customer&apos;s Credit Limit.
        </Typography>
        <Typography component="small">
          3.3 If a Transaction or a delivery of any Bulk Products at any time results in the Customer exceeding its
          Credit Limit (“Credit Limit Exceedance”), the Customer/Cardholder must:
          <br />
          (a) pay Ampol the amount of the Credit Limit Exceedance immediately in full; and
          <br />
          (b) if the Customer/Cardholder is not able to pay in accordance with subclause
          <br />
          (a), immediately return any Products or Bulk Products which are capable of being returned in their original
          condition to Ampol or the Merchant.
        </Typography>
        <Typography component="small">
          <strong>4. Receipts</strong>
        </Typography>
        <Typography component="small">
          4.1 Ampol will issue the Customer with a sales voucher or a delivery docket at the time of the Transaction or
          delivery.
        </Typography>
        <Typography component="small">
          4.2 The Customer agrees that it is the Customer&apos;s/ Cardholder&apos;s responsibility to ensure any sales voucher or
          delivery docket correctly records the type of the Product,&nbsp;Bulk Product or Service, quantity, price and
          other details of the purchase.
        </Typography>
        <Typography component="small">
          <strong>5. Payment</strong>
        </Typography>
        <Typography component="small">
          5.1 Ampol will issue a tax invoice to the Customer for all purchases made during the previous billing period
          and all fees, charges and moneys otherwise due and payable to Ampol.&nbsp;
        </Typography>
        <Typography component="small">
          5.2 The Customer must pay the full amount of the tax invoice by the date specified in the tax invoice (“Due
          Date”).
        </Typography>
        <Typography component="small">
          5.3 The payment will be effected by direct debit from the Customer&apos;s nominated bank account (unless a
          different payment method is agreed in advance in writing between the Customer and Ampol) in accordance with
          the direct debit form in the Application. The Customer agrees to be bound by the Direct Debit Service
          Agreement.
        </Typography>
        <Typography component="small">
          5.4 If the Due Date falls on a non-business day, the payment must be made:
          <br />
          (a) if the payment is effected by direct debit, on the first business day after the Due Date; and&nbsp;
          <br />
          (b) if the payment is effected by any other payment method, on the business day preceding the Due Date.
        </Typography>
        <Typography component="small">
          5.5 The Customer must make all payments in full without deduction or set-off.
        </Typography>
        <Typography component="small">
          5.6 The Customer must notify Ampol of any dispute of any amount of an invoice within 30 days from the date of
          the receipt of the tax invoice. Unless the Customer disputes the amount within 30 days, the amount shall be
          taken to be accepted by the Customer, due and payable to Ampol and not open to dispute or challenge.
        </Typography>
        <Typography component="small">
          5.7 To the extent permitted by law, all money received by Ampol from the Customer will be applied in the
          manner and order determined by Ampol.
        </Typography>
        <Typography component="small">
          <strong>6. Default</strong>
        </Typography>
        <Typography component="small">
          6.1 The payment is made on the date when Ampol receives cleared funds into its nominated bank account.
        </Typography>
        <Typography component="small">
          6.2 The Customer is in default if the full payment of the tax invoice is not made by or on the Due Date or if
          the payment subsequently dishonours.
        </Typography>
        <Typography component="small">
          6.3 If the Customer is in default, in addition to any other right Ampol may have, Ampol may, at its
          discretion:
          <br />
          (a) charge the Customer interest on the overdue amount at a rate of the then current Westpac Banking
          Corporation overdraft rate plus 2% per annum from the Due Date until Ampol receives the payment in full;
          and/or
          <br />
          (b) immediately and without prior notice suspend the provision of either or both of the AmpolCard Facility and
          Bulk Facility to the Customer until the payment is made in full; and /or
          <br />
          (c) review and adjust the Customer&apos;s Credit Limit as Ampol considers appropriate; and/or
          <br />
          (d) terminate the provision of either or both of the AmpolCard Facility and/or Bulk Facility to the Customer
          immediately without prior notice.
        </Typography>
        <Typography component="small">
          6.4 Ampol will endeavour to notify the Customer prior to the suspension or termination of the AmpolCard and/or
          Bulk Facility if the Customer is in default, however, Ampol reserves the right to suspend or terminate the
          AmpolCard and/or Bulk Facility immediately and without prior notice if Ampol forms a view that its actions in
          that respect are justified in the circumstances.
        </Typography>
        <Typography component="small">
          6.5 If the Customer is in default, the Customer will be liable to Ampol for the following:
        </Typography>
        <Typography component="small">
          (a) the unpaid amount; and
          <br />
          (b) any new amount in respect of any purchases and/or fees incurred on the Account after the date of the
          latest tax invoice issued to the Customer;
          <br />
          (c) any interest on the unpaid amount and any new amount charged to the Customer under clause 6.3(a);
          <br />
          (d) if direct debit or other payment method is dishonoured, the Dishonour Fee; and
          <br />
          (e) all costs and expenses, including full legal and administrative costs incurred by Ampol in attempting to
          enforce payment or otherwise incurred as a result of the default. All such amounts are debt incurred by the
          Customer to Ampol and must be paid within 3 business days after the receipt of a tax invoice from Ampol.
          However, the Customer agrees that if a tax invoice has previously been issued for any amount (such as an
          invoice for the unpaid amount), such invoice does not need to be reissued under this clause and that amount
          becomes immediately due and payable on the date of default.
        </Typography>
        <Typography component="small">
          <strong>7. Security and Security Deposit</strong>
        </Typography>
        <Typography component="small">
          7.1 For the purposes of securing payment of all moneys due and payable to Ampol by the Customer arising from
          the use of the AmpolCard Facility and/or Bulk Facility, the Customer:
          <br />
          (a) agrees to deliver to Ampol, within 7 days of written demand, a mortgage in respect of the Customer&apos;s
          Property in a form acceptable to Ampol together with any documents required to obtain registration of the
          mortgage;
          <br />
          (b) charges to Ampol all its Property (wherever situated) to secure its obligations under this Agreement and
          authorises and consents to Ampol lodging a caveat (in a form and subject to any conditions as Ampol sees fit)
          upon the title to the Property in Ampol&apos;s absolute discretion; and
          <br />
          (c) irrevocably appoints Ampol and persons nominated by Ampol separately as the attorney of the Customer with
          power to sign and lodge such caveat or other similar document to give effect to these provisions.
        </Typography>
        <Typography component="small">
          7.2 (a) Ampol may (in its sole discretion), require that the Customer pay to Ampol the Security Deposit and
          withdraw further credit from the Customer.&nbsp;
        </Typography>
        <Typography component="small">
          (b)&nbsp;The Security Deposit must be paid by the Customer in accordance with the Security Deposit Notice and
          may be applied by Ampol towards satisfaction of any amounts due and owing (but unpaid) by the Customer
          pursuant to the terms of these Standard Terms and Conditions. The Security Deposit (to the extent that it has
          not been applied) will otherwise be refunded on termination of these Standard Terms and Conditions.
        </Typography>
        <Typography component="small">
          <strong>8. Risk and Title and PPSA</strong>
        </Typography>
        <Typography component="small">
          8.1 Risk in the Products and Bulk Products will pass to the Customer when the Products or Bulk Products have
          passed into the Customer&apos;s vehicle, tank or other storage equipment or when the Products or Bulk Products have
          been otherwise delivered and unloaded at the Customer&apos;s site or when the convenience store goods have been
          taken out of the store.
        </Typography>
        <Typography component="small">
          8.2 (a) Notwithstanding that the Customer has possession of the Products and/or Bulk Products (Goods), title
          to the Goods remains with Ampol, and no legal or equitable interest in the Goods whatsoever will pass to the
          Customer, until the full purchase amount for the Goods has been paid.
        </Typography>
        <Typography component="small">
          (b) Until title passes to the Customer, the Customer holds the Goods on Ampol&apos;s behalf as bailee and
          acknowledges that a fiduciary relationship exists between Ampol and the Customer.
        </Typography>
        <Typography component="small">
          (c)&nbsp;The Customer may dispose of the Goods to a bona fide sub purchaser provided that such disposals are
          in the ordinary course of the Customer&apos;s business and that the Customer assigns to Ampol (the assignment being
          absolute and not by way of security) all monetary proceeds received by or on behalf of the Customer in respect
          to the Goods.
        </Typography>
        <Typography component="small">
          (d) The Customer agrees that: (i) it holds the proceeds of re-supply of all Goods on trust for and as agent
          for Ampol (ii) it must maintain records of all disposals of the Goods and must permit inspection of these
          records by Ampol promptly upon request, and (iii) it will indemnify Ampol against any claim, action, damage,
          loss, liability, cost, expense that Ampol suffers, incurs or is liable for in respect of Customer&apos;s exercise
          of its rights under this clause 8.2.
        </Typography>
        <Typography component="small">
          (e) The Customer acknowledges that these Standard Terms and Conditions create a security interest for the
          purposes of the PPSA in all Goods supplied, and that Ampol may register a Financing Statement to perfect its
          security interest in the Goods delivered or to be delivered to the Customer in accordance with the provisions
          of the PPSA.
        </Typography>
        <Typography component="small">
          (f)&nbsp;The Customer shall provide all information, execution or arrange for execution of all documents and
          do all other things that Ampol may require, to ensure that Ampol has a perfected first ranking security
          interest in the Goods under the PPSA.
        </Typography>
        <Typography component="small">
          (g)&nbsp;The Customer waives its rights to receive a verification statement in respect of any Financing
          Statement or Financing Change Statement registered by or on behalf of Ampol under the PPSA to the extent
          permitted by the PPSA.
        </Typography>
        <Typography component="small">
          (h)&nbsp;Until the Customer has made full payment for Goods it shall not without the prior written consent of
          Ampol, create a security interest in the Goods or the sale proceeds of the Goods to any third party or permit
          any lien over the goods or the sale proceeds of the goods.
        </Typography>
        <Typography component="small">
          <strong>9. Termination</strong>
        </Typography>
        <Typography component="small">
          9.1 The innocent party may terminate these Standard Terms and Conditions immediately by notice in writing to
          the other party if:
          <br />
          (a) the other party commits a material breach of any obligation under these Standard Terms and Conditions and
          fails to remedy the breach within 14 days after notice from the innocent party to remedy the breach;
          <br />
          (b) the other party breaches any obligation under these Standard Terms and Conditions and the breach cannot be
          remedied;
          <br />
          (c) the other party goes into liquidation, receivership, administration, bankruptcy, enters an arrangement or
          compromise with its creditors, has any form of insolvency administrator appointed to it or to any of its
          property or cannot pay its debts when they are due.
        </Typography>
        <Typography component="small">
          9.2 Ampol may terminate these Standard Terms and Conditions immediately without prior notice if:
          <br />
          (a) the Customer is in default, in accordance with clause 6.3(d); or
          <br />
          (b) the Customer, in Ampol&apos;s reasonable opinion, represents a credit risk to Ampol or may not be able to pay
          moneys owing or which may become owing &nbsp;to Ampol as the payments become due; or
          <br />
          (c) (where the Customer is a partnership), one or more partners become bankrupt or the partnership is
          dissolved;
          <br />
          (d) the Customer dies; or
          <br />
          (e) the Customer fails to purchase in any period any agreed minimum annual quantities of Products or Bulk
          Products.
        </Typography>
        <Typography component="small">
          9.3 Either party may terminate these Standard Terms and Conditions at any time by giving the other party 30
          days written notice.
        </Typography>
        <Typography component="small">
          <strong>10. Effects of Termination</strong>
        </Typography>
        <Typography component="small">
          10.1 On termination of these Standard Terms and Conditions:
          <br />
          (a) without limiting in any way clause 6.5, all money owing to Ampol by the Customer immediately becomes due
          and payable without prejudice to any other right of Ampol;
          <br />
          (b) a party may take such action as it is entitled to take by law, and, for the purposes of the recovery of
          the Products or Bulk Products, Ampol may enter any site where the Products or Bulk Products are stored and
          take possession of them; and
          <br />
          (c) any right accrued prior to the termination remains unaffected.
        </Typography>
        <Typography component="small">
          <strong>11. Limitation of Liability</strong>
        </Typography>
        <Typography component="small">
          11.1 Ampol will not, under any circumstances, be liable to the Customer, whether directly or indirectly for:
          <br />
          (a) the act or omission of the Merchants, the provision of or the quality or standard of the Services provided
          by any Merchant;
          <br />
          (b) any fault with any automatic or electronic facility used in connection with or to process the Ampol Card
          Facility;
          <br />
          (c) any fault with any Card (including PIN);
          <br />
          (d) any fault with the Customer&apos;s tanks, storage equipment, loading and unloading facilities, site or other
          delivery locations;
          <br />
          (e) any third party claim arising out of supply of Products, Bulk Products or
          <br />
          Services or the performance or non-performance of any obligations under these Standard Terms and Conditions.
        </Typography>
        <Typography component="small">
          11.2 All express or implied guarantees, warranties, representations, or other terms and conditions relating to
          the Products, Bulk Products or Services which are not stated in these Standard Terms and Conditions are
          excluded to the maximum extent permitted by law.
        </Typography>
        <Typography component="small">
          11.3 Nothing in these Standard Terms and Conditions excludes, restricts or modifies any right or remedy or any
          guarantee, warranty or other term or condition implied or imposed by any legislation which cannot lawfully be
          excluded or limited. This may include the Australian Consumer Law which contains guarantees that protect the
          purchasers of goods and services in certain circumstances.
        </Typography>
        <Typography component="small">
          11.4 If any guarantee, warranty, term or condition is implied or imposed in relation to these Standard Terms
          and Conditions under the Australian Consumer Law or any other applicable legislation and cannot be excluded (a
          “Non Excludable Provision”) and Ampol is able to limit the Customer&apos;s remedy for breach of the Non Excludable
          Provision, the liability of Ampol for breach of the Non-Excludable Provision is limited to one or more of the
          following at Ampol&apos;s option:
          <br />
          (a) replacement of the defective Products or the supply of equivalent products; or
          <br />
          (b) payment of the cost of replacing the defective Products or of acquiring equivalent products.
        </Typography>
        <Typography component="small">
          11.5 Subject to Ampol&apos;s obligations under the Non-Excludable Provisions, and to the maximum extent permitted
          by law, the maximum aggregate liability of Ampol for all claims under or relating to these Standard Terms and
          Conditions or supply of the Products, Bulk Products and/or Services whether in contract, tort (including
          without limitation negligence), in equity, under statute, or on any other basis, is limited to an amount equal
          to the annual Fee.
        </Typography>
        <Typography component="small">
          11.6 Subject to Ampol&apos;s obligations under the Non-Excludable Provisions, and to the maximum extent permitted
          by law, Ampol is not liable for and no measure of damages will, under any circumstances, include:
          <br />
          (a) special, indirect, consequential, incidental or punitive damages; or
          <br />
          (b) damages for loss of profits, revenue, goodwill, or anticipated savings, whether in contract, tort
          (including negligence) in equity, under statute, or on any other basis, whether or not such loss or damage was
          foreseeable.
        </Typography>
        <Typography component="small">
          <strong>12. Indemnity</strong>
        </Typography>
        <Typography component="small">
          12.1 The Customer shall indemnify and hold Ampol harmless against any claim, liability, cost or expense
          arising directly or indirectly out of:
          <br />
          (a) a breach by the Customer of any provision of these Standard Terms and Conditions;
          <br />
          (b) storage, handling and dispensing of any Products or Bulk Products at the Customer&apos;s site or other
          premises;
          <br />
          (c) misuse, whether innocent or wilful, by the Customer of any Card or the AmpolCard Facility or Bulk
          Facility.
        </Typography>
        <Typography component="small">
          <strong>13. GST</strong>
        </Typography>
        <Typography component="small">
          (a) Subject as may otherwise be expressly stated, and subject to the provisions set out below, if any supply
          made under or in connection with these Standard Terms and Conditions by one party (&apos;Supplier&apos;) to the other
          party (&apos;Acquirer&apos;), is subject to GST, the payment for that supply will be increased by an amount equal to the
          GST payable.
          <br />
          (b) The Acquirer will not be obligated to make any payment for either the supply referred to in paragraph (a)
          or on account of the GST referred to in paragraph (a) until the Supplier has issued a tax invoice to the
          Acquirer for the supply to which the payment relates.
          <br />
          (c) If a payment made by one party to the other party is a reimbursement or indemnification of a cost,
          expense, loss or liability incurred by that other party, the payment shall be reduced by an amount for which
          that party is entitled to an input tax credit.
          <br />
          (d) Words or expressions used, which are defined in A New Tax System (Goods and Services Tax) Act 1999, have
          the same meaning.
        </Typography>
        <Typography component="small">
          <strong>14. PPSA</strong>
        </Typography>
        <Typography component="small">
          Unless a contrary intention appears, words or expressions used in this clause 14 that are defined in the
          Personal Property Securities Act 2009 (Cth) (PPSA) have the same meaning as given to them in the PPSA.
        </Typography>
        <Typography component="small">
          (a)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; If at any time Ampol determines that an agreement (or any of the
          transactions contemplated by or under it) creates a security interest in its favour over any personal
          property, Ampol may apply for any registration, or give any notification, in connection with that security
          interest and the Customer must promptly, upon Ampol&apos;s request, do any thing to the satisfaction of the Ampol
          (including, but not limited to, signing and producing documents, getting documents completed or signed,
          obtaining consents and supplying information) to:
        </Typography>
        <Typography component="small">
          (i)&nbsp;provide more effective security over the relevant personal property;
        </Typography>
        <Typography component="small">(ii)&nbsp;ensure that any such security interest in favour of Ampol:</Typography>
        <Typography component="small">
          (A)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; is at all times enforceable, perfected (including, where applicable, by
          registration) and otherwise effective; and
        </Typography>
        <Typography component="small">
          (B)&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ranks as a first priority security interest;
        </Typography>
        <Typography component="small">
          (iii)&nbsp;enable Ampol to prepare and register a Financing Statement or a Financing Change Statement or give
          any notification in connection with that security interest; and
        </Typography>
        <Typography component="small">
          (iv)&nbsp;enable Ampol to exercise any of its rights or perform any of its obligations in connection with any
          such security interest or under the PPSA.
        </Typography>
        <Typography component="small">
          (b)&nbsp;All costs and expenses arising as a result of actions taken by either party pursuant to clause 14 are
          payable by the Customer. The Customer must pay Ampol sums owing pursuant to this clause 14 within 5 days of a
          written request.
        </Typography>
        <Typography component="small">
          (c) If Chapter 4 of the PPSA would otherwise apply to the enforcement of the security interest created under
          these Standard Terms and Conditions, the Customer agrees that the following provisions of the PPSA will not
          apply:
        </Typography>
        <Typography component="small">
          (i)&nbsp;section 95 (notice of removal of accession), to the extent that it requires Ampol to give a notice to
          the Customer;
        </Typography>
        <Typography component="small">
          (ii)&nbsp;section 121(4) (enforcement of liquid assets – notice to grantor);
        </Typography>
        <Typography component="small">
          (iii)&nbsp;section 125 (obligation to dispose of or retain collateral);
        </Typography>
        <Typography component="small">
          (iv)&nbsp;section 130 (notice of disposal), to the extent that it requires Ampol to give a notice to the
          Customer;
        </Typography>
        <Typography component="small">
          (v)&nbsp;&nbsp;paragraph 132(3)(d) (contents of statement of account after disposal);
        </Typography>
        <Typography component="small">(vi)&nbsp;subsection 132(4) (statement of account if no disposal);</Typography>
        <Typography component="small">(vii)&nbsp;section 142 (redemption of collateral); and</Typography>
        <Typography component="small">(viii)&nbsp;section 143 (reinstatement of security agreement).</Typography>
        <Typography component="small">
          (d)&nbsp;Ampol does not need to give the Customer any notice required under the PPSA (including a notice of a
          verification statement under section 157 of the PPSA) unless the requirement for the notice cannot be
          excluded.
        </Typography>
        <Typography component="small">
          (e)&nbsp;Neither party will disclose to a person or entity that is not a party to this agreement information
          of the kind mentioned in section 275(1) of the PPSA unless section 275(7) of the PPSA applies or that
          information is publicly available. The obligations of each party under this clause 19(f) are in addition to
          the any other obligations of the Hirer under or in connection with each Agreement.
        </Typography>
        <Typography component="small">
          <strong>15. Notices</strong>
        </Typography>
        <Typography component="small">
          15.1 AmpolCard Facility: Except as specified in clause 20.2, all notices must be in writing and must be
          delivered, mailed or sent by facsimile or e-mail:&nbsp;
          <br />
          (a) to Ampol, using the Contact Details in clause 26; and
          <br />
          (b) to the Customer, using the Customer&apos;s contact details as nominated in the Application or as otherwise
          advised by the Customer in writing.
        </Typography>
        <Typography component="small">
          15.2 The notice will be deemed served when, if delivered, on delivery; if sent by mail, two business days
          after the date on which the notice was posted; and if sent by facsimile, on confirmation of the successful
          transmission of all pages and, if sent by e-mail, on the receipt of the e-mail by the recipient&apos;s computer
          network.
        </Typography>
        <Typography component="small">
          <strong>16. Force Majeure</strong>
        </Typography>
        <Typography component="small">
          16.1 Ampol will not be responsible for failure or for any losses resulting out of the failure to supply or
          deliver any Products or Bulk Products caused by force majeure, including:
          <br />
          (a) partial or total interruption of transport, fires, strikes, differences with workmen, armed conflict,
          interference of civil or military authority;
          <br />
          (b) curtailment, failure or cessation of supplies of crude oil or any other products from Ampol&apos;s existing,
          future or contemplated sources of supply;
          <br />
          (c) any breakdown of plant, machinery or equipment owned or operated by Ampol necessary for the production of
          Products or Bulk Products or any planned or unplanned shutdown of any refinery owned or operated by Ampol;
          <br />
          (d) Court orders, Government policy or orders including price controls, levies, premiums or surcharges,
          requirement, request or allocation program;
          <br />
          (e) embargoes or other import or export restrictions or any other causes that in the opinion of Ampol are
          beyond its control; or
          <br />
          (f) anything falling under the general meaning of force majeure or the matters specified in this clause, that
          would in the reasonable opinion of Ampol make the performance of this Agreement uneconomic to Ampol.
        </Typography>
        <Typography component="small">
          16.2 If for any such cause Ampol is unable to supply any of the Product or Bulk&nbsp;Products its obligations
          under these Standard Terms and Conditions shall be deemed suspended for the period it is unable to supply. In
          the event of a shortage of such Products or Bulk Products Ampol may, without being deemed to be in breach of
          these Standard Terms and Conditions, ration or apportion its available supplies among such of its Customers as
          it may, in its absolute direction, deem desirable during the period or periods of shortage.
        </Typography>
        <Typography component="small">
          <strong>17. Purchases</strong>
        </Typography>
        <Typography component="small">
          17.1 Ampol agrees to provide the Customer with the AmpolCard Facility for the purposes of purchasing some or
          all of Products and Services (excluding Bulk Products) from Merchants.
        </Typography>
        <Typography component="small">
          17.2 The Customer agrees that the AmpolCard Facility must be used by the Customer wholly or predominantly for
          business purposes.
        </Typography>
        <Typography component="small">
          17.3 The Customer must pay the prices charged by the Merchant for Products and Services at the time of the
          Transaction, unless different prices are agreed in advance between the Customer and Ampol in writing.
        </Typography>
        <Typography component="small">
          17.4 In addition to the price for Products and Services, the Customer must pay:
          <br />
          (a) any duties, taxes, government charges, levies and other imposts presently or in the future being levied by
          any government or regulatory authority on any of the Products or Services, or the price of any of the Products
          or Services, in the amounts applicable from time to time;
          <br />
          (b) any costs incurred by Ampol in order to comply with the present and/or future requirements of any
          government or regulatory authority, including any requirements relating to pollution, carbon, emissions and
          greenhouse gases (including reporting requirements, the holding of permits and the holding of off-set
          credits), to the extent such costs increase the cost of any Products or Services or the cost of production or
          delivery of any of the Products or Services;
          <br />
          (c) a Card Fee, as agreed between the Customer and Ampol;
          <br />
          (d) a Transaction Fee, as agreed between the Customer and Ampol;
          <br />
          (e) any Card reissue fee, if Ampol reissues any Card to the Customer, unless otherwise agreed in advance
          between the Customer and Ampol in writing;
          <br />
          (f) any Surcharge; and
          <br />
          (g) GST, where applicable.
        </Typography>
        <Typography component="small">
          17.5 Ampol will charge all amounts due for the purchases of Products and Services to the&nbsp;Customer&apos;s
          AmpolCard Account.
        </Typography>
        <Typography component="small">
          <strong>18. Use of Cards</strong>
        </Typography>
        <Typography component="small">
          18.1 Ampol may issue any Cards to the Customer for the persons or vehicles nominated in the Application.
        </Typography>
        <Typography component="small">
          18.2 Subject to clause 20, the Customer, as the AmpolCard Facility holder, is responsible for the use of the
          Cards by any of the Cardholders, including the use of PINs.
        </Typography>
        <Typography component="small">
          18.3 The Cardholder must provide the Card to the Merchant at the time of the Transaction.
        </Typography>
        <Typography component="small">
          18.4 The Customer may only use the Cards which are valid and, subject to earlier suspension and termination,
          only until the expiry date shown on the Card. Subject to clause 20, the Customer shall be responsible for any
          purchases, fees and charges incurred on expired, suspended or otherwise invalid Cards.
        </Typography>
        <Typography component="small">
          18.5 All Cards remain the property of Ampol at all times. Ampol may, (provided it has a legitimate business
          reason to do so, or the Customer is in breach of these Standard Terms and Conditions), at any time, cancel or
          require the Customer to return or destroy any Card. Where the Customer is not otherwise in breach of these
          Standard Terms and Conditions, Ampol will reissue a replacement Card to the Customer as soon as practicable.
        </Typography>
        <Typography component="small">
          18.6 The Cards may not be used to obtain cash, cash equivalent of a Product, such as a gift card, or Service
          or cash for the refund of a Product or Service.
        </Typography>
        <Typography component="small">
          18.7 The Cards may be issued with Purchase Limits as requested in the Application or otherwise notified by
          Ampol to the Customer. It is the Customer&apos;s responsibility to ensure that the Cardholders do not exceed the
          Purchase Limits. The Customer is responsible for any purchase in excess of the Purchase Limits, applying at
          the time of Purchase. Ampol may refuse to supply any Products or Services to the Customer /Cardholder in
          excess of the Purchase Limit (“Purchase Limit Exceedance”). In this case, the Customer /Cardholder must pay
          Ampol /Merchant the amount of the Purchase Limit Exceedance immediately in full. If the Customer/Cardholder is
          unable to pay in full for the Products supplied, the Cardholder must immediately return any Products which are
          capable of being returned in their original condition to the Merchant.
        </Typography>
        <Typography component="small">
          18.8&nbsp;The Customer must have an active Everyday Rewards Account in order to link it to their Card and
          participate in the Woolworths Everyday Rewards Scheme (<strong>Rewards Scheme</strong>). Participation in the
          Rewards Scheme is subject to eligibility criteria and the&nbsp;
          <Anchor variant='small' href={Links.LOYALTY_PROGRAM_REWARDS_SCHEME}>
            AmpolCard Loyalty Program Rewards Scheme Terms and Conditions
          </Anchor>
          . Participation in the Rewards Scheme and the Customer&apos;s ability to earn and redeem Everyday Rewards Points
          will also be subject to the&nbsp;
          <Anchor variant='small' href={Links.WOOLWORTHS_REWARDS}>Everyday Rewards Term and Conditions</Anchor>. The
          Customer acknowledges and agrees that the terms of the Rewards Scheme may be amended by Ampol and/or in
          accordance with the&nbsp;
          <Anchor variant='small' href={Links.LOYALTY_PROGRAM_REWARDS_SCHEME}>
            AmpolCard Loyalty Program Rewards Scheme Terms and Conditions
          </Anchor>
          &nbsp;and/or the&nbsp;
          <Anchor variant='small' href={Links.WOOLWORTHS_REWARDS}>Everyday Rewards Term and Conditions</Anchor>
          .
        </Typography>
        <Typography component="small">
          18.9 Customer may enable any of the Card(s) issued to be used by the relevant Cardholder as their nominated
          payment method in their FuelPay account.
        </Typography>
        <Typography component="small">
          18.10 Once a Card is nominated as a Cardholder&apos;s payment method for FuelPay, the Customer will be notified by
          email. Customer is responsible for ensuring that the Card has been linked to the authorised Cardholder&apos;s
          FuelPay account, and reporting any activity which it suspects to be authorised or fraudulent in accordance
          with clause 20.1.
        </Typography>
        <Typography component="small">
          18.11 The Customer may elect to disable a Card from use in conjunction with FuelPay at any time in its
          absolute discretion. When FuelPay is disabled on a card either via email notification to&nbsp;
          <Anchor variant="small" href={Links.AMPOL_CARD_EMAIL}>ampolcard@ampol.com.au</Anchor>
          &nbsp;or via&nbsp;
          <Anchor variant="small" href={Links.MY_AMPOL_LOGIN}>myAmpol,</Anchor>
          &nbsp;the change will take up to fifteen (15) minutes to take effect.
        </Typography>
        <Typography component="small">
          18.12 Any use of a Card for FuelPay will be governed by the Ampol Application Terms of Use available
          (https://www.ampol.com.au/service-stations/ampol-app/legals) and all Cardholders must comply with these if
          their Card is their nominated payment method in FuelPay.
        </Typography>
        <Typography component="small">
          18.13 If a Cardholder nominates their Card as their FuelPay payment method, they must keep their physical Card
          with them at all times. FuelPay is not available at non-Ampol sites and Ampol cannot guarantee that FuelPay
          will always be available at Ampol participating Locations.
        </Typography>
        <Typography component="small">
          18.14 If Ampol suspects that an AmpolCard being used via FuelPay is doing so fraudulently, Ampol reserves the
          right to disable FuelPay on the card without prior notice.
        </Typography>

        <Typography component="small">
          <strong>19. PINs and Identity Verification</strong>
        </Typography>
        <Typography component="small">
          19.1 Ampol is able to issue Personal Identification Number (“PIN”) activated Cards, with the PIN to be chosen
          by the Customer. Where a Card is PIN activated, the Customer/Cardholder must use the PIN for all electronic
          Transactions. Ampol will not accept/ process any electronic Transaction where a Card is PIN activated and the
          Cardholder fails to enter the correct PIN at the time of the Transaction. In this case, the
          Customer/Cardholder must pay to Ampol/Merchant the amount payable in respect of the Products and/ or Services,
          which have been supplied or performed, immediately in full. If the Customer /Cardholder is unable to pay in
          full for the Products supplied, the Cardholder must immediately return any Products which are capable of being
          returned in their original condition to the Merchant.
        </Typography>
        <Typography component="small">
          19.2 Where a Card has been issued without a PIN or where the Merchant processes the&nbsp;Card manually, the
          Cardholder must provide the Merchant with a suitable form of identification at the time of the Transaction.
          The Customer agrees that the signature on the back of the Card is not a sufficient form of identification.
          Ampol may refuse to supply any Products or Services to the Customer/Cardholder where the Cardholder is not
          able to provide acceptable identification to the Merchant at the time of the Transaction. In this case, the
          Customer/Cardholder must pay to Ampol/ Merchant the amount payable in respect of the Products and/or Services,
          which have been supplied or performed, immediately in full. If the Customer/ Cardholder is not able to pay in
          full for the Products supplied, the Cardholder must immediately return any Products which are capable of being
          returned in their original condition to the Merchant.
        </Typography>
        <Typography component="small">
          19.3 Phone (verbal) password:
          <br />
          A phone (verbal) password (Phone Password) may be set by the Customer as an additional security measure, using
          myAmpol.&nbsp; Once set, any request by the Customer via telephone to make changes to the Customer&apos;s AmpolCard
          Account and Cards will require the Customer to provide the Phone Password. Ampol will not process any requests
          for changes if the Customer fails to provide the correct Phone Password.
          <br />
        </Typography>
        <Typography component="small">
          Phone Passwords are user specific and must be 4-8 alpha/numeric characters. The Customer is responsible for
          keeping the Phone Password confidential and for the use of the Phone Password.
          <br />
        </Typography>
        <Typography component="small">
          The successful provision of the Phone Password by any person will be considered by Ampol as conclusive proof
          that the person providing the password has been authorised.
        </Typography>
        <Typography component="small">
          <strong>20. Lost, Stolen Cards and Cancellation of Authority to Use</strong>
        </Typography>
        <Typography component="small">
          20.1 If any Card is lost, stolen, misused, no longer required by the Customer, or the Customer wishes to
          withdraw the authority of the Cardholder to use the Card, the Customer must notify Ampol immediately either
          through:
          <br />
          (a) AmpolCard Support; or
          <br />
          (b) myAmpol. If the Customer uses myAmpol, the Customer&apos;s notice or request may not be processed for up to 2
          hours from the time the notice or request is made. The Customer will remain fully liable for any use of the
          Card, whether authorised or unauthorised, within this 2 hour period.
        </Typography>
        <Typography component="small">
          20.2 The Customer must NOT use fax or e-mail to give notices under clause 20.1.
        </Typography>
        <Typography component="small">
          20.3 The Customer is fully liable for any purchases, losses, costs, expenses or claims arising out of any use
          of a Card, whether authorised or unauthorised, until Ampol receives a notice from the Customer strictly in
          accordance with clause 20.1.
        </Typography>
        <Typography component="small">
          <strong>21. Odometer Readings and Registration</strong>
        </Typography>
        <Typography component="small">
          21.1 The Customer acknowledges that odometer readings are recorded purely for reporting purposes and Ampol
          takes no responsibility for their administration or accuracy.
        </Typography>
        <Typography component="small">
          21.2 If a Card is set up to require an odometer reading, the Cardholder is responsible for entering a correct
          odometer reading at the time of the Transaction.
        </Typography>
        <Typography component="small">
          21.3 If the Cardholder fails to enter an odometer reading or enters an incorrect odometer reading, the
          electronic Card processing system will still accept the Card and process the transaction. Ampol is not liable
          for any loss, cost, expense or claim arising out of any failure of or error relating to odometer readings.
        </Typography>
        <Typography component="small">
          21.4 The Customer agrees that neither Ampol nor Merchants are able to verify the accuracy of odometer readings
          entered by the Cardholder.
        </Typography>
        <Typography component="small">
          21.5 The Customer agrees that neither Ampol nor Merchants are able to verify the accuracy of motor vehicle
          registration at the time of the Transaction. The Customer agrees that motor vehicle registration is not a form
          of verification for the Card or in relation to any Transaction. Ampol relies on the Customer for the provision
          of correct motor vehicle registration numbers in connection with any Card. Ampol does not verify the
          registration numbers&apos; accuracy or correctness.
        </Typography>
        <Typography component="small">
          <strong>22. Purchases</strong>
        </Typography>
        <Typography component="small">
          22.1 Ampol agrees to provide the Customer with the Bulk Facility for the purposes of purchasing some or all of
          Bulk Products from Ampol on credit.
        </Typography>
        <Typography component="small">
          22.2 The Customer agrees that the Bulk Facility must be used by the Customer wholly or predominantly for
          business purposes.
        </Typography>
        <Typography component="small">
          <strong>23. Prices for Bulk Products</strong>
        </Typography>
        <Typography component="small">
          23.1 The Customer must pay the prices which are charged by Ampol for Bulk Products:
          <br />
          (a) which are delivered to the Customer, at the time of the delivery; or
          <br />
          (b) which are picked up by the Customer from Ampol&apos;s premises, at the time of the pick-up, unless different
          prices have been agreed in advance between the Customer and Ampol in writing.
        </Typography>
        <Typography component="small">
          23.2 In addition to the prices for Bulk Products, the Customer must pay:
        </Typography>
        <Typography component="small">
          (a) any duties, taxes, government charges, levies and other imposts(including import and excise costs, custom
          duties and sales tax) presently or in the future being levied by any government or regulatory authority on any
          of the Bulk Products, or the price of any of the Bulk Products, in the amounts applicable from time to time;
          <br />
          (b) any costs incurred by Ampol in order to comply with the present and/or future requirements of any
          government or regulatory authority, including any requirements relating to pollution, carbon, emissions and
          greenhouse gases (including reporting requirements, the holding of permits and the holding of off-set
          credits), to the extent such costs increase the cost of any Bulk Products or the cost of production or
          delivery of any of the Bulk Products;
          <br />
          (c) delivery charges applicable at the time of delivery. Ampol may change its delivery charges at any time
          without notice to the Customer; and&nbsp;
          <br />
          (d) GST, where applicable.
        </Typography>
        <Typography component="small">
          23.3 Ampol will charge all amounts due for the purchases of Bulk Products to the Customer&apos;s Bulk Account.
        </Typography>
        <Typography component="small">
          23.4 The Customer must not on-supply or resell the Bulk Products. If the Customer on supplies or resells any
          of the Bulk Products, Ampol may, without prejudice to any other entitlements it may have as a consequence,
          change the price for all or any of Bulk Products supplied to the Customer to a price charged or chargeable
          from time to time in relation to equivalent products by a reseller operating from premises nearest the
          relevant location/s.
        </Typography>
        <Typography component="small">
          <strong>24. Delivery</strong>
        </Typography>
        <Typography component="small">
          24.1 Ampol is not obliged to deliver Bulk Products to the Customer at locations other than the Customer&apos;s site
          nominated for delivery in the Application, or as otherwise may be agreed by Ampol.
        </Typography>
        <Typography component="small">
          24.2 Subject to clauses 24.3 and 24.4, Ampol will deliver Bulk Products to the Customer either in agreed
          quantities or in accordance with the Customer&apos;s Order, as the case may be.
        </Typography>
        <Typography component="small">
          24.3 Unless otherwise previously agreed between the Customer and Ampol, the Customer must take delivery in
          quantities not less than the full capacity of bulk tankers used by Ampol to make Bulk Product deliveries.
        </Typography>
        <Typography component="small">
          24.4 Ampol is not obliged to supply any Bulk Products to the Customer in any one calendar month in quantities
          in excess of 5% above the Customer&apos;s average monthly requirements for the period of either preceding 12 months
          or the number of months from the beginning of the Bulk Facility, whichever is the shorter.
        </Typography>
        <Typography component="small">
          24.5 The Customer must make available safe and unrestricted access for delivery at the
          <br />
          Customer&apos;s site.
        </Typography>
        <Typography component="small">
          24.6 The Customer:
          <br />
          (a) is fully responsible for the condition, reliability and safety (including environmental safety) of its
          tanks and other Bulk Products storage equipment;
          <br />
          (b) without limiting paragraph (a), must ensure that its tanks and other Bulk Products storage equipment
          comply with all relevant Laws and that adequate environmental protection systems are in place to prevent
          migration of any Bulk Products (including bunding, oily water separation systems, fire extinguishers and spill
          kits); and
          <br />
          (c) must immediately notify Ampol in writing of any faulty tanks or other Bulk Products storage equipment, in
          which case Ampol will not be obliged to deliver Bulk Products to the Customer until such fault is rectified.
          At the commencement of the Bulk Facility the Customer must certify to Ampol in writing that Proper Procedures
          have been adopted for handling, storing and monitoring Bulk Products in the Customers&apos; tanks and other storage
          equipment (“Certificate”).&nbsp;
        </Typography>
        <Typography component="small">
          24.7 Whether or not the Customer provides Ampol with the Certificate, Ampol will not be responsible for any
          leakage from any of the Customer&apos;s tanks or other storage equipment, nor any associated environmental damage,
          except to the extent of Ampol&apos;s negligent act or omission, and the Customer agrees to indemnify Ampol against
          all actions, claims, costs, expenses and other liability in relation to such leakage or environmental damage.
        </Typography>
        <Typography component="small">
          24.8 Written notice of any claim for any defect in quality or shortage in quantity in relation to any of the
          Bulk Products must be given by the Customer to Ampol within two (2) business days after receipt of the Bulk
          Product for which the claim is made. The notice must state the nature of the defect or deficiency and indicate
          where and when Ampol may inspect the Bulk Product. Any inspection by Ampol for the purposes of this clause
          shall not of itself be construed as an acceptance by Ampol of the Customer&apos;s claim.
        </Typography>
        <Typography component="small">24.9 The Customer must:</Typography>
        <Typography component="small">
          (a) comply with all Laws in relation to the handling, storage and transport of Bulk Products; and
          <br />
          (b) without limiting paragraph (a), ensure that the Customer&apos;s actions and the Customer&apos;s delivery
          requirements under any Order or otherwise, do not encourage or require any driver assigned to carry out any
          delivery to:
          <br />
          (1) exceed permitted driving hours;
          <br />
          (2) fail to have minimum rest periods;
          <br />
          (3) exceed the speed limit;
          <br />
          (4) carry Products that exceed vehicle dimension limits;
          <br />
          (5) carry Products that cause vehicle mass limits to be exceeded;
          <br />
          (6) carry any Products on Ampol&apos;s behalf that are not appropriately secured;
          <br />
          (7) work from a dangerous height; or
          <br />
          (8) breach any Laws.
        </Typography>
        <Typography component="small">
          24.10 The Customer agrees to provide Ampol with unrestricted access to the Customer&apos;s&nbsp;site from time to
          time to conduct an audit of the Customer&apos;s compliance with this clause 24 and Ampol is not obliged to deliver
          Bulk Products to the Customer until any identified faults are rectified.
        </Typography>
        <Typography component="small">
          24.11 If the Customer is unable to accept delivery in accordance with the Customer&apos;s Order or other notified
          requirements, then the Customer must pay a Redirection Fee to Ampol.
        </Typography>
        <Typography component="small">
          <strong>25. Miscellaneous</strong>
        </Typography>
        <Typography component="small">
          25.1 The Customer must notify Ampol in writing of any change in the Customer&apos;s contact details or other
          details within 2 business days of any such change.
        </Typography>
        <Typography component="small">
          25.2 Ampol may:
          <br />
          (a) vary any material provision of these Standard Terms and Conditions at any time by giving not less than 30
          days prior written notice (where it is reasonably necessary to protect Ampol&apos;s legitimate business interests);
          <br />
          and
          <br />
          (b) make minor variations to these Standard Terms and Conditions at any time without giving the Customer prior
          notice. Any new version of the varied Standard Terms and Conditions will be available on Ampol&apos;s public
          website for the Customer&apos;s information.
        </Typography>
        <Typography component="small">
          25.3 These Standard Terms and Conditions will be governed by the laws of NSW.
        </Typography>
        <Typography component="small">
          25.4 Ampol may assign and/or subcontract its rights and obligations under these Standard Terms and Conditions
          without notice at any time.
        </Typography>
        <Typography component="small">
          25.5 The Customer may not assign or otherwise dispose of its rights and obligations under these Standard Terms
          and Conditions without prior written consent from Ampol.
        </Typography>
        <Typography component="small">
          25.6 Any waiver by Ampol of any rights under these Standard Terms and Conditions will not constitute a general
          waiver of those or other rights.
        </Typography>
        <Typography component="small">
          <strong>26. Definitions and Interpretation</strong>
        </Typography>
        <Typography component="small">
          &nbsp;“Account” means the account opened by Ampol for the Customer to operate either or both of the AmpolCard
          Facility and/or Bulk facility;
          <br />
          “Ampol” means&nbsp; Ampol Australia Petroleum Pty. Ltd. &nbsp;A.B.N. 17 000 032 128, or one of its
          subsidiaries, related bodies corporate (as defined in the Corporations Act) or associated companies as the
          context requires;
          <br />
          “AmpolCard Facility” means the facility to purchase Products and/or Services on credit using the Card;
          <br />
          “AmpolCard Support” means telephone 1300 365 096;
          <br />
          “Application” means a credit application provided by Ampol which the Customer must complete to make an
          application to Ampol for the supply of Products and Services on credit;
          <br />
          “Bulk Facility” means the facility to purchase Bulk Products on credit;&nbsp;
          <br />
          “Bulk Products” means Petroleum Products and Lubricants which the Customer purchases in bulk;
          <br />
          <br />
          “Card” means an AmpolCard issued to the Customer under the AmpolCard Facility;
          <br />
          “Card Fee” means a monthly fee (inclusive of GST) for each Card as notified by Ampol to the Customer, except
          where otherwise agreed between Ampol and the Customer;
          <br />
          “Cardholder” means a person who uses a Card with the authority of the Customer;
          <br />
          “Credit Limit” means the amount notified to the Customer by Ampol from time to time as the maximum amount
          allowed for purchases of Products and Services by the Customer on its
          <br />
          Account;
          <br />
          “Customer” means the “Applicant” in the Application;
        </Typography>
        <Typography component="small">
          “Financing Statement” has the same meaning as that expression in the PPSA
        </Typography>
        <Typography component="small">
          “Financing Change Statement” has the same meaning as that expression in the PPSA
          <br />
          “Guarantee, Indemnity and Charge” means an agreement which is signed by the Guarantor or as a part of this
          agreement where relevant;
          <br />
          “Direct Debit Service Agreement” means the agreement regarding the direct debit method of payment;
          <br />
          “Dishonour Fee” is a fee Ampol charges when the Customer&apos;s payment is dishonoured by the
          <br />
          Customer&apos;s financial institution;&nbsp;
          <br />
          “Due Date” means the date, as agreed between the Customer and Ampol, by or on which the amount of the tax
          invoice must be paid in full, or, if no date has been agreed,&nbsp;
          <br />
          (a)&nbsp;For card accounts opened from 1st February 2017, the 14th day of the month following the month in
          which purchases have been made, unless negotiated otherwise in writing with Ampol; and&nbsp;
          <br />
          (b)&nbsp;For card accounts opened on or before 31st January 2017and Bulk customers, the 21st day of the month
          following the month in which purchases have been made;
          <br />
          “GST” means Goods and Services Tax as defined in A New Tax System (Goods and Services Tax) Act 1999;
          <br />
          “Laws” means the requirements of all statutes, rules, regulations, proclamations, awards, ordinances, bylaws
          or Australian Standards, present or future, and whether state, federal or otherwise;
          <br />
          “Lubricants” means all lubricating oils, greases, brake fluids, coolants, fuel performance additives and
          degreasers manufactured or marketed (or both) by or on behalf of Ampol;
          <br />
          “Merchant” means an authorised supplier of some or all of Products and Services and who may accept Cards;
          <br />
          “myAmpol” means www.my.ampol.com.au;
          <br />
          “Order” means either a one off or a periodic order from the Customer for the delivery of Bulk Products;
          <br />
          “Petroleum Products” means petrol, diesel, liquefied petroleum gas and any other products which may be used in
          propelling motor vehicles and which are manufactured or marketed (or both) by or on behalf of Ampol;
        </Typography>
        <Typography component="small">“PPSA” mean the Personal Property Securities Act 2009 (Cth)</Typography>
        <Typography component="small">
          “PPSA Security Interest” means a security interest as defined in s12 of the PPSA
        </Typography>
        <Typography component="small">
          “PPSR” means the register established under section 147 of the PPSA
          <br />
          “Products” means Petroleum Products, Lubricants and convenience store goods;
          <br />
          “Proper Procedures” means procedures which, having regard to the nature of the product being handled and
          stored, its propensity to cause harm to health, safety or the environment and relevant legislation and
          industry codes, a reasonable person would consider appropriate;
          <br />
          “Property” means all property of the Customer, wherever situated, including without limitation all real and
          personal property, business and trading assets, stock, choses in action, goodwill and uncalled capital and
          called but unpaid capital from time to time;
          <br />
          “Purchase Limit” means a daily and/or monthly dollar, volume or transaction type limit;
          <br />
          “Redirection Fee” means the amount determined by Ampol (acting reasonably) equivalent to the cost to Ampol of
          the Customer not accepting delivery of Products, including administrative, transport and storage costs;
        </Typography>
        <Typography component="small">
          “Security Deposit” means the security deposit to be paid to [Ampol] in accordance with the Security Deposit
          Notice.
        </Typography>
        <Typography component="small">
          “Security Deposit Notice” means a written notice from [Ampol] to the Customer requiring that the Customer pay
          the a nominated amount to the account contained in the notice within [5] Business Days.
          <br />
          “Services” means vehicle maintenance, repairs and other services which may be provided by the Merchant to the
          Customer;&nbsp;
          <br />
          “Surcharge” means a fee determined by the Merchant from time to time and charged by the
          <br />
          Merchant to the Customer;&nbsp;
          <br />
          “Transaction” means a purchase transaction for which a Card is used by a Cardholder to purchase Products
          and/or Services;&nbsp;
          <br />
          “Transaction Fee” means an additional amount (fee) (inclusive of GST) paid by the
          <br />
          Customer each time the Customer makes a Transaction, as notified by Ampol to the
          <br />
          Customer, except where otherwise agreed between Ampol and the Customer.
        </Typography>
        <Typography component="small">
          <strong>INTERPRETATION</strong>
        </Typography>
        <Typography component="small">
          Unless the context requires otherwise, the singular includes the plural and vice versa, reference to a gender
          includes all genders, reference to “person” includes a natural person, company, body corporate or other form
          of legal entity, and reference to “including” and “includes” is to read as if followed by “without
          limitation”.
        </Typography>
        <Typography component="small">
          <strong>27. Contact Details</strong>
        </Typography>
        <Typography component="small">
          The Customer may contact Ampol using these contact details:&nbsp;
          <br />
          AmpolCard Support: 1300 365 096&nbsp;
          <br />
          myAmpol
          <br />
          <Anchor variant='small' href={Links.MY_AMPOL_LOGIN}>https://my.ampol.com.au</Anchor>
          <br />
          Address: GPO Box 3998 Sydney NSW 2001&nbsp;
          <br />
          Fax: (02) 92505797&nbsp;
          <br />
          E-mail:&nbsp;
          <Anchor variant='small' href={Links.AMPOL_CARD_EMAIL}>ampolcard@ampol.com.au</Anchor>
        </Typography>
      </div>

      <div>
        <Typography component="h4" id='section-2'>Ampol Roadside Assistance Terms and Conditions</Typography>
        <Typography component="small">
          <strong>Ampol Roadside Assistance Terms and Conditions</strong>
        </Typography>
        <Typography component="small">
          <strong>1. Definitions and Interpretation</strong>
        </Typography>
        <Typography component="small">
          “Accident” means a collision between the Customer&apos;s Vehicle and another vehicle or object.
          <br />
          “Agreement” means this agreement for the provision of Services.&nbsp;
          <br />
          “Breakdown” means a mechanical or electrical failure, which causes the Vehicle to be immobilised or renders it
          unsafe to drive, provided this is not occasioned by attempted or actual theft, attempted or actual break in or
          Accident related damage. A Breakdown may also mean a driver-related incident including, but not limited to,
          basic call outs such as a deflated tyre, locked or lost keys, insufficient fuel or flat battery.&nbsp;
          <br />
          “Business Day” refers to a day that is not a Saturday, a Sunday or a public holiday in any state or territory.
          <br />
          “Ampol” means Ampol Australia Petroleum Pty. Ltd. &nbsp;A.B.N. 17 000 032 128 .&nbsp;
          <br />
          “Services” means those services set out in clause 4.&nbsp;
          <br />
          “Service Provider” means a third party subcontracted to provide roadside assistance at the sole discretion of
          Ampol.&nbsp;
          <br />
          “AmpolCard Standard Terms and Conditions” means the terms and conditions between the Customer and Ampol
          governing the provision of the AmpolCard Facility and the use of AmpolCard (if applicable).&nbsp;
          <br />
          “Vehicle” means vehicles conforming to one of the descriptions listed, namely: passenger, 4x4 recreational and
          light commercial vehicles under 3,500 kg GVM (including payload at the time of breakdown) which are not
          greater than 2.3 metres wide, 5.5 metres in length or 2.0 metres in height, and registered with Ampol for the
          Services.
          <br />
          Words defined in the AmpolCard Standard Terms and Conditions and used in this Agreement have the same
          meaning.&nbsp;
        </Typography>
        <Typography component="small">
          <strong>2. Eligibility for Service&nbsp;</strong>
        </Typography>
        <Typography component="small">
          In order to be eligible to receive/request Services, the Customer must select a Roadside Assistance policy
          when applying for an AmpolCard account, via the Ampol online self-service platform, or via direct contact with
          the Ampol Customer Service team on 1300 365 096 or ampolcard@ampol.com.au.
        </Typography>
        <Typography component="small">
          A Customer will be able to receive/request a Service the next Business Day after signing up to the policy.
        </Typography>
        <Typography component="small">
          <strong>3. Customer Service Request</strong>
        </Typography>
        <Typography component="small">
          3.1 In order to request a Service, the Customer must phone Ampol on 1800 225 111 and provide:
        </Typography>
        <Typography component="small">3.1.1. the 16 digit AmpolCard number;</Typography>
        <Typography component="small">3.1.2. name of the Customer (or the Customer&apos;s representative);</Typography>
        <Typography component="small">3.1.3. make and registration of the Vehicle;</Typography>
        <Typography component="small">3.1.4. location of the Vehicle;</Typography>
        <Typography component="small">
          3.1.5. telephone number by which Ampol can contact the Customer (or the Customer&apos;s representative), while the
          Customer is waiting for Service; and
        </Typography>
        <Typography component="small">3.1.6. any other relevant information.</Typography>
        <Typography component="small">
          <strong>4. AmpolCard Standard Terms and Conditions</strong>
        </Typography>
        <Typography component="small">
          4.1 Where the Customer&apos;s AmpolCard facility is governed by the AmpolCard Standard Terms and Conditions, then
          those AmpolCard Standard Terms and Conditions are incorporated into this Agreement and apply to this
          Agreement.
        </Typography>
        <Typography component="small">
          4.2 In the context that an Ampol Reseller may agree with its eligible AmpolCard customers to procure Ampol to
          provide Roadside Assistance for them, the Ampol Reseller may do so on the terms set out in this document
          except that clause 4.1 will not be applicable.
        </Typography>
        <Typography component="small">
          <strong>5. Services</strong>
        </Typography>
        <Typography component="small">
          5.1 Following a request under clause 3.1, Ampol will provide the following services:
        </Typography>
        <Typography component="small">
          5.1.1. roadside assistance restricted to rectification where possible and safe to do so of the most common
          Breakdown related problems including inflation of a flat tyre, replacement of a flat tyre with the Vehicle&apos;s
          serviceable spare, assistance with using a tyre repair kit (sealant gel and inflation pack) to effect a
          temporary repair and jump starting of a flat battery;
        </Typography>
        <Typography component="small">
          5.1.2. minor roadside repairs restricted to carrying out minor Breakdown related repairs where it is possible
          and safe to do so at the roadside. If major parts or factory diagnostic equipment is required, or where the
          spare tyre is not serviceable, roadworthy or compatible, or where multiple wheels require changing, or where
          the repair kit is not suitable to affect a temporary repair, the Vehicle will be transported to the Customer&apos;s
          preferred destination within the towing limits set out in clause 5.1.6;
        </Typography>
        <Typography component="small">
          5.1.3. replacement of battery and parts, such as hoses and some electrical components, to effect mobilisation
          of a Breakdown repair are at the cost of the Customer;
        </Typography>
        <Typography component="small">
          5.1.4. fuel sufficient to enable the Customer to drive to a refuelling station, where government regulations
          permit, or transport the Vehicle to the nearest refuelling station, in circumstances where the Vehicle runs
          out of fuel;
        </Typography>
        <Typography component="small">
          5.1.5. arranging access to the vehicle, or delivery of the Customer&apos;s spare key, or a locksmith attendance, or
          a tow where the key has been lost or stolen or locked inside the Vehicle up to $150 (including GST). Subject
          to satisfactory proof of ownership, or authority to drive the Vehicle. Any excess cost, and the cost of
          replacement keys is for the Customer. Where the Customer requires the Vehicle to be broken into to recover
          keys locked inside the Vehicle, the Customer is responsible for any loss or damage that occurs to the Vehicle;
        </Typography>
        <Typography component="small">
          5.1.6. arranging towing services to transport the Vehicle, for up to 30 kilometres from the breakdown location
          to the Customer&apos;s preferred destination. Towing cost in excess of 30 kilometres, or towing provided in the
          event that a vehicle has been incorrectly fuelled or towing of a caravan or trailer being towed by the Vehicle
          which has experienced a mechanical is for the Customer&apos;s cost, payable on demand to Ampol;
        </Typography>
        <Typography component="small">
          5.1.7. rescuing the Vehicle should it become bogged on a road that is legally accessible by a conventional two
          wheel drive vehicle, up to a maximum value of $150 (including GST) including the use of special equipment such
          as power winches or extended cables. Any additional cost is for the Customer; and
        </Typography>
        <Typography component="small">
          5.1.8. coordinating a one way taxi of up to $55 (including GST) from the breakdown site to the Customers
          destination in the event the Vehicle has been cleared for towing. This is restricted to metropolitan areas
          only. The Customer is responsible for any additional taxi costs. 5.2 The Customer will pay Ampol for any
          additional services provided to the Customer (including for the avoidance of doubt, those listed in clause 5.
        </Typography>
        <Typography component="small">
          5.2 The Customer will pay Ampol for any additional services provided to the Customer (including for the
          avoidance of doubt, those listed in clause 5.
        </Typography>
        <Typography component="small">
          <strong>6. Exclusions From Services</strong>
        </Typography>
        <Typography component="small">
          6.1 Ampol shall not be held responsible for and is not required to provide any Services where any of the
          following occur:
        </Typography>
        <Typography component="small">
          6.1.1. where the Vehicle has been modified for racing, trials or rallying or any claim for Service has arisen
          in respect of such races, trials, rallies or participation in such activities or where the Vehicle is involved
          in an Accident;
        </Typography>
        <Typography component="small">
          6.1.2. where the Vehicle is carrying more passengers or towing a greater weight than that for which it was
          designed as stated in the manufacturers specifications or arising from or in connection with the improper,
          unauthorised, reckless or negligent operation of the Vehicle or misuse of the Vehicle;
        </Typography>
        <Typography component="small">
          6.1.3. where events occur that are outside of both the Customer and the Service Provider&apos;s control, including
          and not limited to a natural disaster (such as flood, storm or fire), or happening as a result of hostilities
          whether war has been declared or not, riots, sabotage, and industrial dispute that place extraordinary demands
          on the provision of service;
        </Typography>
        <Typography component="small">
          6.1.4. provision of Services outside Australia (and for the purposes of this agreement, “Australia” includes
          the mainland and Tasmania but excludes any other off-shore territory or Island);
        </Typography>
        <Typography component="small">
          6.1.5. a person who was driving the Vehicle without a valid licence issued by a competent authority requests a
          Service;
        </Typography>
        <Typography component="small">
          6.1.6. mechanical Breakdown is due to driver related damage or misuse of the Vehicle other than deflated tyre,
          locked or lost keys, insufficient fuel or flat battery;
        </Typography>
        <Typography component="small">
          6.1.7. Vehicle has not been regularly serviced in accordance with the manufacturer&apos;s instructions;
        </Typography>
        <Typography component="small">
          6.1.8. any damage to or theft of objects and accessories left in or outside of the Vehicle;
        </Typography>
        <Typography component="small">
          6.1.9. Cost of Breakdowns resulting from unauthorised repairs or faulty workmanship.
        </Typography>
        <Typography component="small">
          6.1.10. where the Vehicle is not accessible by two wheel drive recovery Vehicles; or
        </Typography>
        <Typography component="small">
          6.1.11. where the Vehicle is found to be unattended when the Service Provider arrives on the scene.
        </Typography>
        <Typography component="small">
          <strong>7. Payment Obligation</strong>
        </Typography>
        <Typography component="small">
          Where the Customer&apos;s AmpolCard facility is governed by the AmpolCard Standard Terms and Conditions, then:
        </Typography>
        <Typography component="small">
          7.1 Ampol will include on the tax invoice issued to the Customer in accordance with the AmpolCard Standard
          Terms and Conditions the following amounts:
        </Typography>
        <Typography component="small">(a) monthly charge for these Services; and</Typography>
        <Typography component="small">
          (b) any other costs payable by the Customer pursuant to this Agreement.
        </Typography>
        <Typography component="small">
          7.2 The Customer will pay the full amount of the tax invoice by the Due Date in accordance with its
          obligations under the AmpolCard Standard Terms and Conditions.
        </Typography>
        <Typography component="small">
          <strong>8. Privacy Clause</strong>
        </Typography>
        <Typography component="small">
          The Customer acknowledges and agrees that where Ampol subcontracts the Services, subject to the Privacy Act
          1988, Ampol is entitled to and will disclose personal information of the Customer to any such third party
          Service Provider.
        </Typography>
        <Typography component="small">
          <strong>9. Termination</strong>
        </Typography>
        <Typography component="small">
          In the event that the Customer&apos;s right to use the AmpolCard Facility under the AmpolCard Standard Terms and
          Conditions terminates, this Agreement will also terminate immediately on termination of the AmpolCard Standard
          Terms and Conditions.
        </Typography>
        <Typography component="small">
          Ampol may terminate this Agreement at any time if Ampol gives the Customer not less than 5 business days&apos;
          notice in writing (and for the avoidance of doubt, termination of this Agreement will not affect any AmpolCard
          Standard Terms and Conditions in place between the parties regarding the Customer&apos;s AmpolCard facility).
        </Typography>
        <Typography component="small">
          <strong>10. Variations</strong>
        </Typography>
        <Typography component="small">
          In addition to its rights under the AmpolCard Standard Terms and Conditions, Ampol may vary these terms and
          conditions where any third party Service Provider engaged by Ampol in connection with providing the Services
          changes, or Ampol provides any services directly.
        </Typography>
      </div>

      <div>
        <Typography component="h4" id="section-3">Ampol Commercial Roadside Assistance Terms and Conditions</Typography>
        <Typography component="small">
          <strong>1. Definitions and Interpretation</strong>
        </Typography>
        <Typography component="small">
          “Accident” means a collision between the Customer&apos;s Vehicle and another vehicle or object.&nbsp;
          <br />
          “Agreement” means this agreement for the provision of Services.
          <br />
          “Breakdown” means a mechanical or electrical failure, which causes the Vehicle to be immobilised or renders it
          unsafe to drive, provided this is not occasioned by attempted or actual theft, attempted or actual break in or
          Accident related damage.&nbsp; A Breakdown may also mean a driver-related incident including, but not limited
          to, basic call outs such as a deflated tyre, locked or lost keys, insufficient fuel or flat battery.
          <br />
          “Business Day” refers to a day that is not a Saturday, a Sunday or a public holiday in any state or territory.
          <br />
          “Ampol” means Ampol Australia Petroleum Pty. Ltd. &nbsp;A.B.N. 17 000 032 128.
          <br />
          “Country Areas” means an area outside of a Metropolitan Area as determined from time to time by the Service
          Provider at the Service Provider&apos;s absolute discretion.&nbsp;
          <br />
          “Metropolitan Areas” means the metropolitan areas of each capital city of Australia as determined from time to
          time by the Service Provider at the Service Provider&apos;s absolute discretion.&nbsp;
          <br />
          “Services” means those services set out in clause 5.
          <br />
          “Service Provider” means a third party subcontracted to provide roadside assistance at the sole discretion of
          Ampol.&nbsp;
          <br />
          “AmpolCard Standard Terms and Conditions” means the terms and conditions between the Customer and Ampol
          governing the provision of the AmpolCard Facility and the use of AmpolCard (if applicable).
          <br />
          “Commercial Vehicle” a Light Commercial Vehicle or a Heavy Commercial Vehicle&nbsp;&nbsp;
          <br />
          &nbsp;weighing in excess of 3,500 GVM
          <br />
          “Light Commercial Vehicle” means a vehicle weighing more than 3.5 tonnes GVM but no more than 12 tonnes GVM
          <br />
          “Heavy Commercial Vehicle” means a vehicle weighing more than 12 tonnes GVN&nbsp;
          <br />
          “Trailer” means any two, three or four wheeled registered domestic trailer, caravan, horse trailer, dog
          trailer, pig trailer, articulated trailer, boat trailer, or B-Double trailer.
          <br />
          <br />
          Words defined in the AmpolCard Standard Terms and Conditions and used in this Agreement have the same meaning.
        </Typography>
        <Typography component="small">
          <strong>2. Eligibility for Service&nbsp;</strong>
        </Typography>
        <Typography component="small">
          In order to be eligible to receive/request Services, the Customer must select a Roadside Assistance policy
          when applying for an AmpolCard account, via the Ampol online self-service platform, or via direct contact with
          the Ampol Customer Service team on 1300 365 096 or ampolcard@ampol.com.au.
        </Typography>
        <Typography component="small">
          A Customer will be able to receive/request a Service the next Business Day after signing up to the policy.
        </Typography>
        <Typography component="small">
          <strong>3. Customer Service Request</strong>
        </Typography>
        <Typography component="small">
          3.1 In order to request a Service, the Customer must phone Ampol on 1800 225 111 and provide:
          <br />
          3.1.1 the 16-digit AmpolCard number;&nbsp;
          <br />
          3.1.2 name of the Customer (or the Customer&apos;s representative);&nbsp;
          <br />
          3.1.3 make and registration of the Commercial Vehicle;
          <br />
          3.1.4 location of the Commercial Vehicle;&nbsp;
          <br />
          3.1.5 telephone number by which Ampol can contact the Customer (or the Customer&apos;s representative), while the
          Customer is waiting for Service; and&nbsp;
          <br />
          3.1.6 any other relevant information.
        </Typography>
        <Typography component="small">
          <strong>4. AmpolCard Standard Terms and Conditions</strong>
        </Typography>
        <Typography component="small">
          4.1 Where the Customer&apos;s AmpolCard facility is governed by the AmpolCard Standard Terms and Conditions, then
          those AmpolCard Standard Terms and Conditions are incorporated into this Agreement and apply to this
          Agreement.&nbsp;&nbsp;
          <br />
          4.2 In the context that an Ampol Reseller may agree with its eligible AmpolCard customers to procure Ampol to
          provide Roadside Assistance for them, the Ampol Reseller may do so on the terms set out in this document
          except that clause 4.1 will not be applicable.
        </Typography>
        <Typography component="small">
          <strong>5. Services</strong>
        </Typography>
        <Typography component="small">
          5.1 Following a request under clause 3.1, Ampol will provide the following services:
          <br />
          5.1.1 roadside assistance restricted to rectification where possible and safe to do so of the most common
          Breakdown related problems including inflation of a flat tyre, replacement of a flat tyre with the Commercial
          Vehicle&apos;s serviceable spare provided the Customer can provide suitable safe working equipment and, assistance
          with using a tyre repair kit (sealant gel and inflation pack ) to effect a temporary repair and jump starting
          of a flat battery;
          <br />
          5.1.2 minor roadside repairs restricted to carrying out minor Breakdown related repairs where it is possible
          and safe to do so at the roadside.&nbsp; If major parts or factory diagnostic equipment is required, or where
          the spare tyre is not serviceable, roadworthy or compatible, or where multiple wheels require changing, or
          where the repair kit is not suitable to affect a temporary repair, or where the Customer cannot provide
          suitable safe working equipment to change the wheel,&nbsp; the Commercial Vehicle will be transported to the
          Customer&apos;s preferred destination within the towing limits set out in clause 5.1.6;
          <br />
          5.1.3 replacement of battery and parts, such as hoses and some electrical components, to effect mobilisation
          of a Breakdown repair are at the cost of the Customer;
          <br />
          5.1.4 up to 20 litres of fuel to enable the Customer to drive to a refuelling station, where government
          regulations permit, or to transport the Commercial Vehicle to the nearest refuelling station, in circumstances
          where the Commercial Vehicle runs out of fuel;
          <br />
          5.1.5 arranging access to the Commercial Vehicle, or delivery of the Customer&apos;s spare key, or a locksmith
          attendance, or a tow where the key has been lost or stolen or locked inside the Commercial Vehicle up to $150
          (including GST).&nbsp; Subject to satisfactory proof of ownership, or authority to drive the Commercial
          Vehicle.&nbsp; Any excess cost, and the cost of replacement keys is the responsibility of the Customer.&nbsp;
          &nbsp;Where the Customer requires the Commercial Vehicle to be broken into to recover keys locked inside the
          Commercial Vehicle, the Customer is responsible for any loss or damage that occurs to the Commercial
          Vehicle;&nbsp;&nbsp;
          <br />
          5.1.6 arranging towing services to transport the Commercial Vehicle during normal business hours, from the
          breakdown location to the Customer&apos;s preferred destination, however where towing is required outside of normal
          business hours towing services will only be provided up to 20 km from the breakdown location in Metropolitan
          Areas and 50 km from the breakdown location in Country Areas.&nbsp; For a Light Commercial Vehicle towing
          costs in excess of 20 kilometres from the breakdown location in Metropolitan Areas and 50km from the breakdown
          location in Country Areas are the responsibility of the Customer, payable on demand by Ampol, For Heavy
          Commercial Vehicles all towing costs are the responsibility of the Customer, payable on demand by Ampol,&nbsp;
          <br />
          5.1.7 Following a Breakdown, arranging towing services for Trailers towed by a Commercial Vehicle where the
          Trailer is required to be towed to the same destination as the Commercial Vehicle. Costs associated with
          towing a Trailer are the responsibility of the Customer, payable on demand by Ampol.
          <br />
          5.1.8 rescuing the Commercial Vehicle should it become bogged on a road that is legally accessible by a
          conventional two wheel drive vehicle, up to a maximum value of $150 (including GST) including the use of
          special equipment such as power winches or extended cables.&nbsp; &nbsp;Any additional cost is for the
          Customer; and
          <br />
          5.1.9 coordinating a one way taxi of up to $55 (including GST) from the breakdown site to the Customers
          destination in the event the Heavy Vehicle has been cleared for towing. The Customer is responsible for any
          additional taxi costs.
          <br />
          5.2 The Customer will pay Ampol for any additional services provided to the Customer (including for the
          avoidance of doubt, those listed in clause 5.
        </Typography>
        <Typography component="small">
          <strong>6. Exclusions From Services</strong>
        </Typography>
        <Typography component="small">
          6.1 Ampol shall not be held responsible for and is not required to provide any Services where any of the
          following occur:
          <br />
          6.1.1 where the Commercial Vehicle or Trailer is unregistered or reasonably considered by the Service Provider
          to be an un-roadworthy vehicle
          <br />
          6.1.2 where repairs have been attempted on the Commercial Vehicle or Trailer immediately following the
          Breakdown by anyone (excluding the Service Provider)
          <br />
          6.1.3 where the Commercial Vehicle or Trailer is partly or fully dismantled.&nbsp;
          <br />
          6.1.4 where the Commercial Vehicle has suffered damage as a result of an accident, collision, flood, theft,
          fire or malicious damage.&nbsp;
          <br />
          6.1.5 where a Commercial Vehicle or Trailer has been driven or transported to any licensed motor vehicle
          repairer.&nbsp;
          <br />
          6.1.6 where a Commercial Vehicle continues to be driven against Ampol, or the Service Provider&apos;s advice or the
          advice of a licensed repairer.&nbsp;
          <br />
          6.1.7 where the Commercial Vehicle has been modified for racing, trials or rallying or any claim for Service
          has arisen in respect of such races, trials, rallies or participation in such activities or where the
          Commercial Vehicle is involved in an Accident;
          <br />
          6.1.8 where the Commercial Vehicle is carrying more passengers or towing a greater weight than that for which
          it was designed as stated in the manufacturers specifications or arising from or in connection with the
          improper, unauthorised, reckless or negligent operation of the Commercial Vehicle or misuse of the Commercial
          Vehicle;
          <br />
          6.1.9 where events occur that are outside of both the Customer and the Service Provider&apos;s control, including
          and not limited to a natural disaster (such as flood, storm or fire), or happening as a result of hostilities
          whether war has been declared or not, riots, sabotage, and industrial dispute that place extraordinary demands
          on the provision of service;
          <br />
          6.1.10 provision of Services outside Australia (and for the purposes of this agreement, “Australia” includes
          the mainland and Tasmania but excludes any other off-shore territory or Island);
          <br />
          6.1.11 a person who was driving the Commercial Vehicle without a valid licence issued by a competent authority
          requests a Service;
          <br />
          6.1.12 mechanical Breakdown is due to driver related damage or misuse of the Commercial Vehicle other than
          deflated tyre, locked or lost keys, insufficient fuel or flat battery;
          <br />
          6.1.13 Commercial Vehicle has not been regularly serviced in accordance with the manufacturer&apos;s instructions;
          <br />
          6.1.14 any damage to or theft of objects and accessories left in or outside of the Commercial Vehicle;
          <br />
          6.1.15 Cost of Breakdowns resulting from unauthorised repairs or faulty workmanship.
          <br />
          6.1.16 where the Commercial Vehicle is not accessible by two wheel drive recovery Vehicles or there is
          restricted access to the Commercial Vehicle and the Service Provider does not have permission to enter; or
          <br />
          6.1.17 where the Commercial Vehicle is found to be unattended when the Service Provider arrives on the
          scene.&nbsp;
          <br />
          6.1.18 where the Commercial Vehicle or Trailer is transporting hazardous or dangerous materials&nbsp;
          <br />
          6.1.19 where the Commercial Vehicle or Trailer has been manufactured or modified in a way which in the Service
          Provider&apos;s opinion will increase the probability of it being damaged during towing.&nbsp;
          <br />
          6.1.20 where the Commercial Vehicle or Trailer cannot be unlocked due to the keys being locked in the
          Commercial Vehicle or Trailer or the keys being lost.
          <br />
          6.1.21 where the Commercial Vehicle or Trailer is carrying live animals unless the Commercial Vehicle or
          Trailer is unloaded to the satisfaction of the Service Provider.&nbsp;
        </Typography>
        <Typography component="small">
          <strong>7. Payment Obligation</strong>
        </Typography>
        <Typography component="small">
          Where the Customer&apos;s AmpolCard facility is governed by the AmpolCard Standard Terms and Conditions, then:
          <br />
          7.1 Ampol will include on the tax invoice issued to the Customer in accordance with the AmpolCard Standard
          Terms and Conditions the following amounts:
          <br />
          (a) monthly charge for these Services; and
          <br />
          (b) any other costs payable by the Customer pursuant to this Agreement.
          <br />
          7.2 The Customer will pay the full amount of the tax invoice by the Due Date in accordance with its
          obligations under the AmpolCard Standard Terms and Conditions.
        </Typography>
        <Typography component="small">
          <strong>8. Privacy Clause</strong>
        </Typography>
        <Typography component="small">
          The Customer acknowledges and agrees that where Ampol subcontracts the Services, subject to the Privacy Act
          1988, Ampol is entitled to and will disclose personal information of the Customer to any such third party
          Service Provider.
        </Typography>
        <Typography component="small">
          <strong>9. Termination</strong>
        </Typography>
        <Typography component="small">
          In the event that the Customer&apos;s right to use the AmpolCard Facility under the AmpolCard Standard Terms and
          Conditions terminates, this Agreement will also terminate immediately on termination of the AmpolCard Standard
          Terms and Conditions.
          <br />
          <br />
          Ampol may terminate this Agreement at any time if Ampol gives the Customer not less than 5 business days&apos;
          notice in writing (and for the avoidance of doubt, termination of this Agreement will not affect any Ampol
          Standard Terms and Conditions in place between the parties regarding the Customer&apos;s AmpolCard facility).
        </Typography>
        <Typography component="small">
          <strong>10. Variations</strong>
        </Typography>
        <Typography component="small">
          In addition to its rights under the AmpolCard Standard Terms and Conditions, Ampol may vary these terms and
          conditions where any third party Service Provider engaged by Ampol in connection with providing the Services
          changes, or Ampol provides any services directly.
        </Typography>
      </div>
    </div>
  );
};

export default RsaTermsAndConditionsContent;
